/**
 * PropertyFilters - Hybrid Rightmove/Zoopla style filter component
 * Features: Essential filters bar, slide-in advanced panel, mobile bottom sheet
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Slider } from './ui/slider';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from './ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import {
  MapPin, PoundSterling, Bed, Home, SlidersHorizontal, Heart, X,
  ChevronDown, ChevronUp, Calendar as CalendarIcon, Search, Filter,
  Map, List, PenTool, Flame
} from 'lucide-react';
import { format } from 'date-fns';
import { useCountry } from '../context/CountryContext';
import SaveSearchModal from './SaveSearchModal';
import RecentSearches, { saveRecentSearch, RecentSearchesBadges } from './RecentSearches';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Property type configurations by country
const PROPERTY_TYPES = {
  base: [
    { value: 'house', label: 'House' },
    { value: 'apartment', label: 'Apartment / Flat' },
    { value: 'studio', label: 'Studio' },
    { value: 'room', label: 'Room' },
  ],
  UK: [
    { value: 'bungalow', label: 'Bungalow' },
    { value: 'maisonette', label: 'Maisonette' },
    { value: 'duplex', label: 'Duplex' },
    { value: 'penthouse', label: 'Penthouse' },
    { value: 'townhouse', label: 'Townhouse' },
    { value: 'terraced', label: 'Terraced House' },
    { value: 'semi-detached', label: 'Semi-detached House' },
    { value: 'detached', label: 'Detached House' },
  ],
  LK: [
    { value: 'annex', label: 'Annex' },
    { value: 'boarding', label: 'Boarding / Hostel' },
    { value: 'upstairs', label: 'Upstairs Unit' },
    { value: 'downstairs', label: 'Downstairs Unit' },
    { value: 'villa', label: 'Villa' },
  ]
};

// Price ranges by country
const PRICE_RANGES = {
  UK: {
    min: [0, 500, 750, 1000, 1250, 1500, 2000, 2500, 3000],
    max: [500, 750, 1000, 1250, 1500, 2000, 2500, 3000, 5000, 10000],
    currency: '£',
    label: 'pcm'
  },
  LK: {
    min: [0, 10000, 20000, 30000, 50000, 75000, 100000],
    max: [20000, 30000, 50000, 75000, 100000, 150000, 200000, 500000],
    currency: 'Rs.',
    label: '/month'
  }
};

// Radius options by country (miles for UK, km for SL)
const RADIUS_OPTIONS = {
  UK: [
    { value: 'this_area', label: 'This area only' },
    { value: '0.25', label: '+ 0.25 miles' },
    { value: '0.5', label: '+ 0.5 miles' },
    { value: '1', label: '+ 1 miles' },
    { value: '3', label: '+ 3 miles' },
    { value: '5', label: '+ 5 miles' },
    { value: '10', label: '+ 10 miles' },
    { value: '15', label: '+ 15 miles' },
    { value: '20', label: '+ 20 miles' },
    { value: '30', label: '+ 30 miles' },
    { value: '40', label: '+ 40 miles' },
  ],
  LK: [
    { value: 'this_area', label: 'This area only' },
    { value: '0.5', label: '+ 0.5 km' },
    { value: '1', label: '+ 1 km' },
    { value: '2', label: '+ 2 km' },
    { value: '5', label: '+ 5 km' },
    { value: '10', label: '+ 10 km' },
    { value: '15', label: '+ 15 km' },
    { value: '20', label: '+ 20 km' },
    { value: '30', label: '+ 30 km' },
    { value: '50', label: '+ 50 km' },
  ]
};

// Bedroom options
const BEDROOM_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'studio', label: 'Studio' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4+', label: '4+' },
];

// Feature options
const FEATURES = {
  popular: [
    { id: 'bills_included', label: 'Bills included' },
    { id: 'pets_allowed', label: 'Pets allowed' },
    { id: 'parking', label: 'Parking' },
    { id: 'garden', label: 'Garden' },
  ],
  more: [
    { id: 'ensuite', label: 'Ensuite' },
    { id: 'balcony', label: 'Balcony' },
    { id: 'lift', label: 'Lift' },
    { id: 'hmo', label: 'HMO Licensed' },
    { id: 'dishwasher', label: 'Dishwasher' },
    { id: 'washing_machine', label: 'Washing Machine' },
  ]
};

// UK-specific features
const UK_FEATURES = [
  { id: 'epc_rating', label: 'EPC Rating C+', type: 'checkbox' },
  { id: 'council_tax_band', label: 'Council Tax Band', type: 'select', options: ['A', 'B', 'C', 'D', 'E', 'F'] },
];

// SL-specific features
const SL_FEATURES = [
  { id: 'generator', label: 'Generator/Backup Power' },
  { id: 'water_supply', label: '24hr Water Supply' },
  { id: 'separate_entrance', label: 'Separate Entrance' },
];

// Filter chip component
const FilterChip = ({ label, onRemove }) => (
  <Badge 
    variant="secondary" 
    className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 hover:bg-blue-200"
    data-testid={`filter-chip-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    {label}
    <X 
      className="w-3 h-3 cursor-pointer hover:text-blue-900" 
      onClick={onRemove}
    />
  </Badge>
);

// Essential Filters Bar Component
const EssentialFiltersBar = ({ 
  filters, 
  onFilterChange, 
  advancedCount, 
  onOpenAdvanced,
  onSaveSearch,
  country,
  resultCount,
  viewMode,
  onViewModeChange
}) => {
  const priceConfig = PRICE_RANGES[country] || PRICE_RANGES.LK;
  const radiusOptions = RADIUS_OPTIONS[country] || RADIUS_OPTIONS.LK;
  
  return (
    <div className="bg-white border-b sticky top-0 z-40 shadow-sm" data-testid="essential-filters-bar">
      <div className="max-w-7xl mx-auto px-4 py-3">
        {/* Desktop Layout */}
        <div className="hidden md:flex items-center gap-3 flex-wrap">
          {/* Location */}
          <div className="flex items-center gap-2 min-w-[180px]">
            <MapPin className="w-4 h-4 text-gray-500" />
            <Input
              placeholder="Search location..."
              value={filters.location || ''}
              onChange={(e) => onFilterChange('location', e.target.value)}
              className="h-9"
              data-testid="location-input"
            />
          </div>
          
          {/* Radius */}
          <Select 
            value={filters.radius || 'this_area'} 
            onValueChange={(v) => onFilterChange('radius', v)}
          >
            <SelectTrigger className="w-32 h-9" data-testid="radius-select">
              <SelectValue placeholder="Radius" />
            </SelectTrigger>
            <SelectContent>
              {radiusOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Price Range */}
          <div className="flex items-center gap-2">
            <PoundSterling className="w-4 h-4 text-gray-500" />
            <Select 
              value={filters.minPrice?.toString() || 'no-min'} 
              onValueChange={(v) => onFilterChange('minPrice', v === 'no-min' ? null : parseInt(v))}
            >
              <SelectTrigger className="w-24 h-9" data-testid="min-price-select">
                <SelectValue placeholder="Min" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-min">No min</SelectItem>
                {priceConfig.min.map(p => (
                  <SelectItem key={p} value={p.toString()}>
                    {priceConfig.currency}{p.toLocaleString()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-gray-400">-</span>
            <Select 
              value={filters.maxPrice?.toString() || 'no-max'} 
              onValueChange={(v) => onFilterChange('maxPrice', v === 'no-max' ? null : parseInt(v))}
            >
              <SelectTrigger className="w-24 h-9" data-testid="max-price-select">
                <SelectValue placeholder="Max" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-max">No max</SelectItem>
                {priceConfig.max.map(p => (
                  <SelectItem key={p} value={p.toString()}>
                    {priceConfig.currency}{p.toLocaleString()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Bedrooms */}
          <div className="flex items-center gap-2">
            <Bed className="w-4 h-4 text-gray-500" />
            <Select 
              value={filters.bedrooms || 'any'} 
              onValueChange={(v) => onFilterChange('bedrooms', v)}
            >
              <SelectTrigger className="w-24 h-9" data-testid="bedrooms-select">
                <SelectValue placeholder="Beds" />
              </SelectTrigger>
              <SelectContent>
                {BEDROOM_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label} {opt.value !== 'any' && opt.value !== 'studio' ? 'bed' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Property Type */}
          <div className="flex items-center gap-2">
            <Home className="w-4 h-4 text-gray-500" />
            <Select 
              value={filters.propertyType || 'any'} 
              onValueChange={(v) => onFilterChange('propertyType', v)}
            >
              <SelectTrigger className="w-32 h-9" data-testid="property-type-select">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any type</SelectItem>
                {PROPERTY_TYPES.base.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
                {(PROPERTY_TYPES[country] || []).slice(0, 3).map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* More Filters Button */}
          <Button 
            variant="outline" 
            onClick={onOpenAdvanced}
            className="h-9 gap-2"
            data-testid="more-filters-btn"
          >
            <SlidersHorizontal className="w-4 h-4" />
            More filters
            {advancedCount > 0 && (
              <Badge variant="secondary" className="ml-1 bg-blue-500 text-white text-xs px-1.5">
                {advancedCount}
              </Badge>
            )}
          </Button>
          
          {/* Save Search */}
          <Button 
            variant="ghost" 
            className="h-9 gap-2 text-gray-600 hover:text-pink-600"
            onClick={onSaveSearch}
            data-testid="save-search-btn"
          >
            <Heart className="w-4 h-4" />
            Save search
          </Button>
          
          {/* View Mode Toggle */}
          <div className="flex border rounded-lg overflow-hidden ml-auto">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              className={`h-9 rounded-none px-3 ${viewMode === 'list' ? 'bg-blue-600' : ''}`}
              onClick={() => onViewModeChange('list')}
              data-testid="list-view-btn"
            >
              <List className="w-4 h-4 mr-1" />
              List
            </Button>
            <Button
              variant={viewMode === 'map' ? 'default' : 'ghost'}
              size="sm"
              className={`h-9 rounded-none px-3 ${viewMode === 'map' ? 'bg-blue-600' : ''}`}
              onClick={() => onViewModeChange('map')}
              data-testid="map-view-btn"
            >
              <Map className="w-4 h-4 mr-1" />
              Map
            </Button>
            <Button
              variant={viewMode === 'draw' ? 'default' : 'ghost'}
              size="sm"
              className={`h-9 rounded-none px-3 ${viewMode === 'draw' ? 'bg-blue-600' : ''}`}
              onClick={() => onViewModeChange('draw')}
              data-testid="draw-view-btn"
            >
              <PenTool className="w-4 h-4 mr-1" />
              Draw
            </Button>
            <Button
              variant={viewMode === 'heat' ? 'default' : 'ghost'}
              size="sm"
              className={`h-9 rounded-none px-3 ${viewMode === 'heat' ? 'bg-orange-500' : ''}`}
              onClick={() => onViewModeChange('heat')}
              data-testid="heat-view-btn"
            >
              <Flame className="w-4 h-4 mr-1" />
              Heat
            </Button>
          </div>
        </div>
        
        {/* Mobile Layout */}
        <div className="flex md:hidden items-center gap-2">
          <div className="flex-1 relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Location"
              value={filters.location || ''}
              onChange={(e) => onFilterChange('location', e.target.value)}
              className="pl-9 h-10"
            />
          </div>
          {/* Mobile Map Toggle */}
          <Button
            variant={viewMode === 'map' ? 'default' : 'outline'}
            size="icon"
            className={`h-10 w-10 ${viewMode === 'map' ? 'bg-blue-600' : ''}`}
            onClick={() => onViewModeChange(viewMode === 'map' ? 'list' : 'map')}
            data-testid="mobile-map-toggle-btn"
          >
            {viewMode === 'map' ? <List className="w-4 h-4" /> : <Map className="w-4 h-4" />}
          </Button>
          <Button 
            variant="outline" 
            onClick={onOpenAdvanced}
            className="h-10 gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
            {advancedCount > 0 && (
              <Badge variant="secondary" className="bg-blue-500 text-white text-xs px-1.5">
                {advancedCount}
              </Badge>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Active Filter Chips
const ActiveFilterChips = ({ filters, onFilterChange, onClearAll, country }) => {
  const priceConfig = PRICE_RANGES[country] || PRICE_RANGES.LK;
  const radiusOptions = RADIUS_OPTIONS[country] || RADIUS_OPTIONS.LK;
  const chips = [];
  
  // Radius chip
  if (filters.radius && filters.radius !== 'this_area') {
    const radiusOpt = radiusOptions.find(r => r.value === filters.radius);
    chips.push({ 
      key: 'radius', 
      label: radiusOpt?.label || `+ ${filters.radius}`, 
      onRemove: () => onFilterChange('radius', 'this_area')
    });
  }
  
  if (filters.minPrice || filters.maxPrice) {
    const label = filters.minPrice && filters.maxPrice 
      ? `${priceConfig.currency}${filters.minPrice.toLocaleString()} - ${priceConfig.currency}${filters.maxPrice.toLocaleString()}`
      : filters.minPrice 
        ? `${priceConfig.currency}${filters.minPrice.toLocaleString()}+`
        : `Up to ${priceConfig.currency}${filters.maxPrice.toLocaleString()}`;
    chips.push({ key: 'price', label, onRemove: () => { onFilterChange('minPrice', null); onFilterChange('maxPrice', null); }});
  }
  
  if (filters.bedrooms && filters.bedrooms !== 'any') {
    chips.push({ 
      key: 'beds', 
      label: `${filters.bedrooms} bed${filters.bedrooms !== '1' && filters.bedrooms !== 'studio' ? 's' : ''}`, 
      onRemove: () => onFilterChange('bedrooms', 'any')
    });
  }
  
  if (filters.propertyType && filters.propertyType !== 'any') {
    const type = [...PROPERTY_TYPES.base, ...(PROPERTY_TYPES[country] || [])].find(t => t.value === filters.propertyType);
    chips.push({ 
      key: 'type', 
      label: type?.label || filters.propertyType, 
      onRemove: () => onFilterChange('propertyType', 'any')
    });
  }
  
  if (filters.furnishing && filters.furnishing !== 'any') {
    chips.push({ 
      key: 'furnishing', 
      label: filters.furnishing === 'furnished' ? 'Furnished' : 'Unfurnished', 
      onRemove: () => onFilterChange('furnishing', 'any')
    });
  }
  
  // Feature chips
  Object.keys(filters.features || {}).forEach(feat => {
    if (filters.features[feat]) {
      const feature = [...FEATURES.popular, ...FEATURES.more].find(f => f.id === feat);
      if (feature) {
        chips.push({ 
          key: feat, 
          label: feature.label, 
          onRemove: () => onFilterChange('features', { ...filters.features, [feat]: false })
        });
      }
    }
  });
  
  if (chips.length === 0) return null;
  
  return (
    <div className="bg-gray-50 border-b px-4 py-2" data-testid="active-filter-chips">
      <div className="max-w-7xl mx-auto flex items-center gap-2 flex-wrap">
        {chips.map(chip => (
          <FilterChip key={chip.key} label={chip.label} onRemove={chip.onRemove} />
        ))}
        {chips.length > 0 && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClearAll}
            className="text-gray-500 hover:text-gray-700 h-7 px-2"
          >
            <X className="w-3 h-3 mr-1" />
            Clear all
          </Button>
        )}
      </div>
    </div>
  );
};

// Advanced Filters Panel
const AdvancedFiltersPanel = ({ 
  filters, 
  onFilterChange, 
  onApply, 
  onClear, 
  country,
  resultCount,
  isOpen,
  onClose 
}) => {
  const [showMoreTypes, setShowMoreTypes] = useState(false);
  const [showMoreFeatures, setShowMoreFeatures] = useState(false);
  
  const allPropertyTypes = [
    ...PROPERTY_TYPES.base,
    ...(PROPERTY_TYPES[country] || [])
  ];
  
  const countryFeatures = country === 'UK' ? UK_FEATURES : SL_FEATURES;
  
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="advanced-filters-panel">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5" />
            More filters
          </SheetTitle>
        </SheetHeader>
        
        <div className="py-6 space-y-6">
          {/* Sort & View */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-700">Sort by</Label>
            <Select 
              value={filters.sortBy || 'best_match'} 
              onValueChange={(v) => onFilterChange('sortBy', v)}
            >
              <SelectTrigger data-testid="sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="best_match">Best match</SelectItem>
                <SelectItem value="newest">Newest first</SelectItem>
                <SelectItem value="price_low">Price (low to high)</SelectItem>
                <SelectItem value="price_high">Price (high to low)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Availability */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-700">Availability</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filters.availableFrom ? format(new Date(filters.availableFrom), 'PPP') : 'Available from'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={filters.availableFrom ? new Date(filters.availableFrom) : undefined}
                  onSelect={(date) => onFilterChange('availableFrom', date?.toISOString())}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="include_let_agreed"
                checked={filters.includeLetAgreed || false}
                onCheckedChange={(checked) => onFilterChange('includeLetAgreed', checked)}
              />
              <Label htmlFor="include_let_agreed" className="text-sm text-gray-600 cursor-pointer">
                Include Let Agreed
              </Label>
            </div>
          </div>
          
          {/* Furnishing */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-700">Furnishing</Label>
            <RadioGroup 
              value={filters.furnishing || 'any'} 
              onValueChange={(v) => onFilterChange('furnishing', v)}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="any" id="furnish_any" />
                <Label htmlFor="furnish_any" className="cursor-pointer">Any</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="furnished" id="furnish_yes" />
                <Label htmlFor="furnish_yes" className="cursor-pointer">Furnished</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="unfurnished" id="furnish_no" />
                <Label htmlFor="furnish_no" className="cursor-pointer">Unfurnished</Label>
              </div>
            </RadioGroup>
          </div>
          
          {/* Property Types (Expanded) */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-700">Property type</Label>
            <div className="grid grid-cols-2 gap-2">
              {allPropertyTypes.slice(0, showMoreTypes ? allPropertyTypes.length : 6).map(type => (
                <div key={type.value} className="flex items-center gap-2">
                  <Checkbox 
                    id={`type_${type.value}`}
                    checked={filters.propertyTypes?.includes(type.value) || filters.propertyType === type.value}
                    onCheckedChange={(checked) => {
                      const current = filters.propertyTypes || [];
                      if (checked) {
                        onFilterChange('propertyTypes', [...current, type.value]);
                      } else {
                        onFilterChange('propertyTypes', current.filter(t => t !== type.value));
                      }
                    }}
                  />
                  <Label htmlFor={`type_${type.value}`} className="text-sm cursor-pointer">
                    {type.label}
                  </Label>
                </div>
              ))}
            </div>
            {allPropertyTypes.length > 6 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowMoreTypes(!showMoreTypes)}
                className="text-blue-600 hover:text-blue-700 p-0 h-auto"
              >
                {showMoreTypes ? 'Show less' : 'Show more types'}
                {showMoreTypes ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
              </Button>
            )}
          </div>
          
          {/* Features (Popular) */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-700">Features (popular)</Label>
            <div className="grid grid-cols-2 gap-2">
              {FEATURES.popular.map(feat => (
                <div key={feat.id} className="flex items-center gap-2">
                  <Checkbox 
                    id={`feat_${feat.id}`}
                    checked={filters.features?.[feat.id] || false}
                    onCheckedChange={(checked) => onFilterChange('features', { ...filters.features, [feat.id]: checked })}
                  />
                  <Label htmlFor={`feat_${feat.id}`} className="text-sm cursor-pointer">
                    {feat.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
          
          {/* More Features */}
          <Collapsible open={showMoreFeatures} onOpenChange={setShowMoreFeatures}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
                <span className="text-sm font-medium text-gray-700">More features</span>
                {showMoreFeatures ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <div className="grid grid-cols-2 gap-2">
                {FEATURES.more.map(feat => (
                  <div key={feat.id} className="flex items-center gap-2">
                    <Checkbox 
                      id={`feat_${feat.id}`}
                      checked={filters.features?.[feat.id] || false}
                      onCheckedChange={(checked) => onFilterChange('features', { ...filters.features, [feat.id]: checked })}
                    />
                    <Label htmlFor={`feat_${feat.id}`} className="text-sm cursor-pointer">
                      {feat.label}
                    </Label>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
          
          {/* Country-specific Features */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-700">
              {country === 'UK' ? 'UK-specific' : 'Sri Lanka-specific'}
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {countryFeatures.map(feat => (
                <div key={feat.id} className="flex items-center gap-2">
                  <Checkbox 
                    id={`country_${feat.id}`}
                    checked={filters.countryFeatures?.[feat.id] || false}
                    onCheckedChange={(checked) => onFilterChange('countryFeatures', { ...filters.countryFeatures, [feat.id]: checked })}
                  />
                  <Label htmlFor={`country_${feat.id}`} className="text-sm cursor-pointer">
                    {feat.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
          
          {/* SL-specific: Part of house filter */}
          {country === 'LK' && (
            <div className="space-y-3">
              <Label className="text-sm font-medium text-gray-700">Part of house</Label>
              <RadioGroup 
                value={filters.partOfHouse || 'any'} 
                onValueChange={(v) => onFilterChange('partOfHouse', v)}
                className="space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="any" id="part_any" />
                  <Label htmlFor="part_any" className="cursor-pointer">Any</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="entire" id="part_entire" />
                  <Label htmlFor="part_entire" className="cursor-pointer">Entire house</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="annex" id="part_annex" />
                  <Label htmlFor="part_annex" className="cursor-pointer">Annex (separate entrance)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="portion" id="part_portion" />
                  <Label htmlFor="part_portion" className="cursor-pointer">Portion (shared entrance)</Label>
                </div>
              </RadioGroup>
            </div>
          )}
        </div>
        
        {/* Footer with Actions */}
        <SheetFooter className="border-t pt-4 flex gap-3">
          <Button variant="outline" onClick={onClear} className="flex-1">
            Clear all
          </Button>
          <Button onClick={onApply} className="flex-1 bg-blue-600 hover:bg-blue-700">
            Show {resultCount?.toLocaleString() || ''} results
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

// Mobile Bottom Sheet (2-step)
const MobileFiltersSheet = ({
  filters,
  onFilterChange,
  onApply,
  onClear,
  country,
  resultCount,
  isOpen,
  onClose
}) => {
  const [step, setStep] = useState(1);
  const priceConfig = PRICE_RANGES[country] || PRICE_RANGES.LK;
  const radiusOptions = RADIUS_OPTIONS[country] || RADIUS_OPTIONS.LK;
  const countryFeatures = country === 'UK' ? UK_FEATURES : SL_FEATURES;
  
  // Reset step when sheet closes
  React.useEffect(() => {
    if (!isOpen) setStep(1);
  }, [isOpen]);
  
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="h-[85vh] rounded-t-xl" data-testid="mobile-filters-sheet">
        <SheetHeader className="border-b pb-3">
          <SheetTitle className="flex items-center justify-between">
            <span>{step === 1 ? 'Filters' : step === 2 ? 'More options' : 'Features'}</span>
            <div className="flex gap-1">
              {[1, 2, 3].map(s => (
                <div 
                  key={s} 
                  className={`w-2 h-2 rounded-full ${step === s ? 'bg-blue-600' : 'bg-gray-300'}`}
                />
              ))}
            </div>
          </SheetTitle>
        </SheetHeader>
        
        {step === 1 ? (
          <div className="py-4 space-y-5 overflow-y-auto max-h-[calc(85vh-180px)]">
            {/* Location & Radius */}
            <div className="space-y-3">
              <Label className="font-medium">Location</Label>
              <Input
                placeholder="Search location..."
                value={filters.location || ''}
                onChange={(e) => onFilterChange('location', e.target.value)}
              />
              <Select 
                value={filters.radius || 'this_area'} 
                onValueChange={(v) => onFilterChange('radius', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Search radius" />
                </SelectTrigger>
                <SelectContent>
                  {radiusOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Price Range */}
            <div className="space-y-3">
              <Label className="font-medium">Price range</Label>
              <div className="flex gap-2">
                <Select 
                  value={filters.minPrice?.toString() || 'no-min'} 
                  onValueChange={(v) => onFilterChange('minPrice', v === 'no-min' ? null : parseInt(v))}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Min" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-min">No min</SelectItem>
                    {priceConfig.min.map(p => (
                      <SelectItem key={p} value={p.toString()}>
                        {priceConfig.currency}{p.toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="self-center text-gray-400">-</span>
                <Select 
                  value={filters.maxPrice?.toString() || 'no-max'} 
                  onValueChange={(v) => onFilterChange('maxPrice', v === 'no-max' ? null : parseInt(v))}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Max" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-max">No max</SelectItem>
                    {priceConfig.max.map(p => (
                      <SelectItem key={p} value={p.toString()}>
                        {priceConfig.currency}{p.toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Bedrooms */}
            <div className="space-y-3">
              <Label className="font-medium">Bedrooms</Label>
              <div className="flex gap-2 flex-wrap">
                {BEDROOM_OPTIONS.map(opt => (
                  <Button
                    key={opt.value}
                    variant={filters.bedrooms === opt.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => onFilterChange('bedrooms', opt.value)}
                    className={filters.bedrooms === opt.value ? 'bg-blue-600' : ''}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Property Type */}
            <div className="space-y-3">
              <Label className="font-medium">Property type</Label>
              <div className="flex gap-2 flex-wrap">
                {[...PROPERTY_TYPES.base, ...(PROPERTY_TYPES[country] || []).slice(0, 2)].map(type => (
                  <Button
                    key={type.value}
                    variant={filters.propertyType === type.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => onFilterChange('propertyType', type.value)}
                    className={filters.propertyType === type.value ? 'bg-blue-600' : ''}
                  >
                    {type.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        ) : step === 2 ? (
          <div className="py-4 space-y-5 overflow-y-auto max-h-[calc(85vh-180px)]">
            {/* Furnishing */}
            <div className="space-y-3">
              <Label className="font-medium">Furnishing</Label>
              <div className="flex gap-2 flex-wrap">
                {['Any', 'Furnished', 'Unfurnished'].map(opt => (
                  <Button
                    key={opt}
                    variant={filters.furnishing === opt.toLowerCase() ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => onFilterChange('furnishing', opt.toLowerCase())}
                    className={filters.furnishing === opt.toLowerCase() ? 'bg-blue-600' : ''}
                  >
                    {opt}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Available From */}
            <div className="space-y-3">
              <Label className="font-medium">Available from</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.availableFrom ? format(new Date(filters.availableFrom), 'PPP') : 'Any date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.availableFrom ? new Date(filters.availableFrom) : undefined}
                    onSelect={(date) => onFilterChange('availableFrom', date?.toISOString())}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Include Let Agreed */}
            <div className="flex items-center gap-2">
              <Checkbox 
                id="mobile_let_agreed"
                checked={filters.includeLetAgreed || false}
                onCheckedChange={(checked) => onFilterChange('includeLetAgreed', checked)}
              />
              <Label htmlFor="mobile_let_agreed" className="cursor-pointer">
                Include Let Agreed
              </Label>
            </div>
            
            {/* SL-specific: Part of house */}
            {country === 'LK' && (
              <div className="space-y-3">
                <Label className="font-medium">Part of house</Label>
                <div className="space-y-2">
                  {[
                    { value: 'any', label: 'Any' },
                    { value: 'entire', label: 'Entire house' },
                    { value: 'annex', label: 'Annex (separate entrance)' },
                    { value: 'portion', label: 'Portion (shared entrance)' }
                  ].map(opt => (
                    <div key={opt.value} className="flex items-center gap-2">
                      <input
                        type="radio"
                        id={`mobile_part_${opt.value}`}
                        checked={filters.partOfHouse === opt.value}
                        onChange={() => onFilterChange('partOfHouse', opt.value)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <Label htmlFor={`mobile_part_${opt.value}`} className="cursor-pointer">
                        {opt.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="py-4 space-y-5 overflow-y-auto max-h-[calc(85vh-180px)]">
            {/* Popular Features */}
            <div className="space-y-3">
              <Label className="font-medium">Popular features</Label>
              <div className="grid grid-cols-2 gap-3">
                {FEATURES.popular.map(feat => (
                  <div key={feat.id} className="flex items-center gap-2">
                    <Checkbox 
                      id={`mobile_feat_${feat.id}`}
                      checked={filters.features?.[feat.id] || false}
                      onCheckedChange={(checked) => onFilterChange('features', { ...filters.features, [feat.id]: checked })}
                    />
                    <Label htmlFor={`mobile_feat_${feat.id}`} className="text-sm cursor-pointer">
                      {feat.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            
            {/* More Features */}
            <div className="space-y-3">
              <Label className="font-medium">More features</Label>
              <div className="grid grid-cols-2 gap-3">
                {FEATURES.more.map(feat => (
                  <div key={feat.id} className="flex items-center gap-2">
                    <Checkbox 
                      id={`mobile_more_${feat.id}`}
                      checked={filters.features?.[feat.id] || false}
                      onCheckedChange={(checked) => onFilterChange('features', { ...filters.features, [feat.id]: checked })}
                    />
                    <Label htmlFor={`mobile_more_${feat.id}`} className="text-sm cursor-pointer">
                      {feat.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Country-specific Features */}
            <div className="space-y-3">
              <Label className="font-medium">
                {country === 'UK' ? 'UK-specific' : 'Sri Lanka-specific'}
              </Label>
              <div className="grid grid-cols-1 gap-3">
                {countryFeatures.map(feat => (
                  <div key={feat.id} className="flex items-center gap-2">
                    <Checkbox 
                      id={`mobile_country_${feat.id}`}
                      checked={filters.countryFeatures?.[feat.id] || false}
                      onCheckedChange={(checked) => onFilterChange('countryFeatures', { ...filters.countryFeatures, [feat.id]: checked })}
                    />
                    <Label htmlFor={`mobile_country_${feat.id}`} className="text-sm cursor-pointer">
                      {feat.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t flex gap-3">
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={onClear} className="flex-1">
                Clear
              </Button>
              <Button onClick={() => setStep(2)} className="flex-1 bg-blue-600 hover:bg-blue-700">
                Next
              </Button>
            </>
          ) : step === 2 ? (
            <>
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                Back
              </Button>
              <Button onClick={() => setStep(3)} className="flex-1 bg-blue-600 hover:bg-blue-700">
                Next
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                Back
              </Button>
              <Button onClick={onApply} className="flex-1 bg-blue-600 hover:bg-blue-700">
                Show {resultCount?.toLocaleString() || ''} results
              </Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Main PropertyFilters Component
export default function PropertyFilters({ 
  onFiltersChange, 
  initialFilters = {},
  resultCount = 0,
  className = '',
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange
}) {
  const { country } = useCountry();
  const [filters, setFilters] = useState({
    location: '',
    radius: 'this_area',
    minPrice: null,
    maxPrice: null,
    bedrooms: 'any',
    propertyType: 'any',
    propertyTypes: [],
    furnishing: 'any',
    availableFrom: null,
    includeLetAgreed: false,
    sortBy: 'best_match',
    features: {},
    countryFeatures: {},
    partOfHouse: 'any',
    searchCenter: null,
    ...initialFilters
  });
  
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [saveSearchOpen, setSaveSearchOpen] = useState(false);
  const [internalViewMode, setInternalViewMode] = useState('list');
  
  // Use external view mode if provided, otherwise use internal
  const viewMode = externalViewMode !== undefined ? externalViewMode : internalViewMode;
  const handleViewModeChange = (mode) => {
    if (externalOnViewModeChange) {
      externalOnViewModeChange(mode);
    } else {
      setInternalViewMode(mode);
    }
  };
  
  // Count active advanced filters
  const advancedCount = useMemo(() => {
    let count = 0;
    if (filters.furnishing && filters.furnishing !== 'any') count++;
    if (filters.availableFrom) count++;
    if (filters.includeLetAgreed) count++;
    if (filters.propertyTypes?.length > 0) count++;
    if (filters.partOfHouse && filters.partOfHouse !== 'any') count++;
    count += Object.values(filters.features || {}).filter(Boolean).length;
    count += Object.values(filters.countryFeatures || {}).filter(Boolean).length;
    return count;
  }, [filters]);
  
  // Geocode location to get coordinates for distance calculation
  const geocodeLocation = useCallback(async (location) => {
    if (!location || location.trim().length < 2) {
      return null;
    }
    try {
      const response = await axios.get(`${API_URL}/api/geocode/`, {
        params: { location, country }
      });
      if (response.data?.latitude && response.data?.longitude) {
        return [response.data.latitude, response.data.longitude];
      }
    } catch (err) {
      console.error('Geocoding failed:', err);
    }
    return null;
  }, [country]);
  
  // Handle filter changes
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => {
      const updated = { ...prev, [key]: value };
      return updated;
    });
  }, []);
  
  // Geocode location when it changes (debounced)
  useEffect(() => {
    if (!filters.location || filters.location.trim().length < 2) {
      // Clear searchCenter if no location
      if (filters.searchCenter) {
        setFilters(prev => ({ ...prev, searchCenter: null }));
      }
      return;
    }
    
    const timeoutId = setTimeout(async () => {
      const coords = await geocodeLocation(filters.location);
      if (coords) {
        setFilters(prev => ({ ...prev, searchCenter: coords }));
      }
    }, 500); // Debounce 500ms
    
    return () => clearTimeout(timeoutId);
  }, [filters.location, geocodeLocation]);
  
  // Notify parent of filter changes
  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange(filters);
    }
  }, [filters, onFiltersChange]);
  
  // Clear all filters
  const handleClearAll = useCallback(() => {
    setFilters({
      location: '',
      radius: 'this_area',
      minPrice: null,
      maxPrice: null,
      bedrooms: 'any',
      propertyType: 'any',
      propertyTypes: [],
      furnishing: 'any',
      availableFrom: null,
      includeLetAgreed: false,
      sortBy: 'best_match',
      features: {},
      countryFeatures: {},
      partOfHouse: 'any',
      searchCenter: null
    });
  }, []);
  
  // Apply filters (for advanced panel)
  const handleApply = useCallback(() => {
    setAdvancedOpen(false);
    setMobileFiltersOpen(false);
    if (onFiltersChange) {
      onFiltersChange(filters);
    }
  }, [filters, onFiltersChange]);
  
  // Detect mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  
  return (
    <div className={className} data-testid="property-filters">
      {/* Essential Filters Bar */}
      <EssentialFiltersBar
        filters={filters}
        onFilterChange={handleFilterChange}
        advancedCount={advancedCount}
        onOpenAdvanced={() => isMobile ? setMobileFiltersOpen(true) : setAdvancedOpen(true)}
        onSaveSearch={() => setSaveSearchOpen(true)}
        country={country}
        resultCount={resultCount}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
      />
      
      {/* Active Filter Chips */}
      <ActiveFilterChips
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearAll={handleClearAll}
        country={country}
      />
      
      {/* Desktop Advanced Filters Panel */}
      <AdvancedFiltersPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        onApply={handleApply}
        onClear={handleClearAll}
        country={country}
        resultCount={resultCount}
        isOpen={advancedOpen}
        onClose={() => setAdvancedOpen(false)}
      />
      
      {/* Mobile Filters Sheet */}
      <MobileFiltersSheet
        filters={filters}
        onFilterChange={handleFilterChange}
        onApply={handleApply}
        onClear={handleClearAll}
        country={country}
        resultCount={resultCount}
        isOpen={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
      />
      
      {/* Save Search Modal */}
      <SaveSearchModal
        isOpen={saveSearchOpen}
        onClose={() => setSaveSearchOpen(false)}
        filters={filters}
        category="property"
        country={country}
        resultCount={resultCount}
      />
    </div>
  );
}

// Helper function to check if category is property
export function isPropertyCategory(slug) {
  return slug === 'property' || 
         slug === 'property-rent' || 
         slug === 'property-sale' ||
         slug === 'rent' ||
         slug === 'for-rent';
}
